import React, { useState, forwardRef } from 'react';
import ReactSelect, { components } from 'react-select';
import classnames from 'classnames';
// import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

const nestedOptionStyles = `
  .nested-option-group {
    display: flex;
    flex-direction: column;
  }

  .nested-children-container {
    display: flex;
    flex-direction: column;
    margin-left: 8px;
  }

  .selectable-group-heading:hover {
    background-color: ##ebeffc !important;
  }

  .leaf-node-container:hover {
    background-color: ##ebeffc;
  }

  .custom-option-container:hover {
    background-color: #ebeffc;
  }
`;

const NestedOptionStyles = () => <style>{nestedOptionStyles}</style>;

const ClearIndicator = (props) => {
  const {
    getStyles,
    innerProps: { ref, ...restInnerProps },
  } = props;
  return (
    <div {...restInnerProps} ref={ref} style={getStyles('clearIndicator', props)}>
      <FontAwesomeIcon icon={faTimes} alt={faTimes.iconName} />
    </div>
  );
};

const renderNestedOption = (
  props,
  label,
  nestedOptions,
  labelKey,
  valueKey,
  index,
  cloneOptionType,
) => {
  const { getStyles, innerProps, selectOption } = props;
  const currentOption = props.data;
  const isSelectable = currentOption && currentOption.id;

  // Check if this option should be disabled based on cloneOptionType
  const isDisabledByType =
    cloneOptionType === 'filesonly' && nestedOptions && nestedOptions.length > 0;

  return (
    <div key={`${label}-${index}`} className="nested-option-group">
      {/* Parent option heading */}
      <div
        style={{
          ...getStyles('groupHeading', props),
          paddingLeft: index * 12,
          cursor: isSelectable && !isDisabledByType ? 'pointer' : 'default',
          color: isSelectable && !isDisabledByType ? 'black' : '#888888',
          padding: '8px 12px',
          borderRadius: '2px',
          fontWeight: 'bold',
          borderBottom: isSelectable && !isDisabledByType ? 'none' : '1px solid #eee',
          // Add visual indication that it's disabled when filesonly mode
          opacity: isDisabledByType ? 0.6 : 1,
        }}
        className={isSelectable && !isDisabledByType ? 'selectable-group-heading' : ''}
        onClick={isSelectable && !isDisabledByType ? () => selectOption(currentOption) : undefined}
        onMouseEnter={(e) => {
          if (isSelectable && !isDisabledByType) {
            e.currentTarget.style.backgroundColor = '#ebeffc';
          }
        }}
        onMouseLeave={(e) => {
          if (isSelectable && !isDisabledByType) {
            e.currentTarget.style.backgroundColor = '';
          }
        }}
      >
        {label}
      </div>

      {/* Container for all children - this creates the visual indentation */}
      <div className="nested-children-container">
        {/* Map through child options with proper indentation */}
        {nestedOptions &&
          nestedOptions.map((nestedOption) => {
            if (nestedOption.options) {
              // This is a nested group - recursively render it with its own children
              return renderNestedOption(
                { ...props, data: nestedOption },
                nestedOption.label,
                nestedOption.options,
                labelKey,
                valueKey,
                index + 1,
                cloneOptionType, // Pass the cloneOptionType to nested calls
              );
            }

            // This is a leaf node - always enable it
            return (
              <div
                style={{
                  marginLeft: index * 2,
                  cursor: 'pointer',
                }}
                key={nestedOption.id || nestedOption[valueKey]}
                className="leaf-node-container"
              >
                <components.Option
                  {...props}
                  data={nestedOption}
                  innerProps={{
                    ...innerProps,
                    onClick: () => selectOption(nestedOption),
                    style: {
                      ...innerProps.style,
                      cursor: 'pointer',
                    },
                  }}
                >
                  {nestedOption[labelKey] || nestedOption.name}
                </components.Option>
              </div>
            );
          })}
      </div>
    </div>
  );
};

const Select = forwardRef(
  (
    {
      defaultValue,
      name,
      onChange,
      className,
      labelKey = 'label',
      valueKey = 'value',
      options,
      isMulti = false,
      getOptionLabel,
      placeholder,
      style,
      isClearable = true,
      readOnly,
      customOption,
      customSingleValue,
      noIndicator,
      isSearchable = true,
      minimal,
      menuPortalTarget,
      menuPosition,
      menuPlacement = 'auto',
      backspaceRemovesValue = true,
      openMenuOnFocus = true,
      cloneOptionType = '',
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const styles = {
      container: (base) => ({
        ...base,
        ...style,
        ...(minimal && { padding: 0, height: 'fit-content' }),
      }),
      control: (base, state) => ({
        ...base,
        border: 0,
        // This line disable the blue border
        boxShadow: 'none',
        ...(state.isDisabled && { backgroundColor: '#F5F5F5' }),
        ...(minimal && { minHeight: 'fit-content', height: 'fit-content' }),
      }),
      option: (base, state) => ({
        ...base,
        display: state.isDisabled ? 'none' : 'block',
        backgroundColor: 'inherit',
        color: 'black',
        cursor: 'pointer',
        padding: '8px 12px',
        ':active': {
          backgroundColor: '#7BA0F4',
        },
        ':hover': {
          backgroundColor: '#ebeffc',
          cursor: 'pointer',
        },
      }),
      groupHeading: (base, state) => ({
        ...base,
        fontSize: '1rem',
        paddingTop: '8px',
      }),
      placeholder: (base, state) => ({
        ...base,
        color: '#DDDDDD',
      }),
      valueContainer: (provided, state) => ({
        ...provided,
        ...(minimal && { padding: '0 0.5rem' }),
      }),
      indicatorsContainer: (provided, state) => ({
        ...provided,
        ...(minimal && { height: 'fit-content', alignSelf: 'center' }),
      }),
      input: (provided, state) => ({
        ...provided,
        ...(minimal && { height: 'fit-content' }),
      }),
      clearIndicator: (provided, state) => ({
        ...provided,
        ...(minimal && { padding: '0 0.5rem' }),
      }),
      menu: (provided, state) => ({ ...provided }),
      menuList: (provided) => ({ ...provided, maxHeight: 240, overflowY: 'auto' }),
      menuPortal: (base) => ({
        // fix for layout settings
        ...base,
        zIndex: 99999,
      }),
      multiValueLabel: (base, state) => {
        return state.data.isFixed ? { ...base, color: '#888888', paddingRight: 6 } : base;
      },
      multiValueRemove: (base, state) => {
        return state.data.isFixed ? { ...base, display: 'none' } : base;
      },
    };

    const onFocusHandle = () => setIsFocused(true);

    const onBlurHandle = () => setIsFocused(false);

    const Option = (props) => {
      const { children, data } = props;
      const nestedOptions = data.options;

      // Check if this is a parent option that should be disabled in filesonly mode
      const shouldDisable =
        cloneOptionType === 'filesonly' && nestedOptions && nestedOptions.length > 0;

      if (nestedOptions) {
        const label = data.label;
        return renderNestedOption(
          props,
          label,
          nestedOptions,
          labelKey,
          valueKey,
          2,
          cloneOptionType,
        );
      }

      // Apply custom styling to non-nested options (always selectable in filesonly mode)
      return (
        <div
          style={{
            cursor: 'pointer',
          }}
          className="custom-option-container"
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#ebeffc';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '';
          }}
        >
          <components.Option
            {...props}
            innerProps={{
              ...props.innerProps,
              style: {
                ...props.innerProps?.style,
                cursor: 'pointer',
              },
            }}
          >
            {children}
          </components.Option>
        </div>
      );
    };

    return (
      <>
        <NestedOptionStyles />
        <ReactSelect
          name={name}
          ref={ref}
          options={options}
          styles={styles}
          className={classnames(className, { focused: !!isFocused }, 'common-lookup')}
          isMulti={isMulti}
          onFocus={onFocusHandle}
          onBlur={onBlurHandle}
          onChange={onChange}
          defaultValue={defaultValue}
          getOptionLabel={(option) => {
            if (getOptionLabel) {
              return getOptionLabel(option);
            }
            return option.label || option[labelKey] || option.name || '';
          }}
          getOptionValue={(option) => option[valueKey]}
          placeholder={placeholder}
          isClearable={isClearable}
          isDisabled={readOnly}
          isSearchable={isSearchable}
          backspaceRemovesValue={backspaceRemovesValue}
          components={{
            Option: customOption ? customOption : Option,
            ...(customSingleValue && { SingleValue: customSingleValue }),
            ...(minimal && { ClearIndicator }),
            ...(noIndicator && { IndicatorSeparator: () => null }),
            ...(noIndicator && { DropdownIndicator: () => null }),
          }}
          menuPlacement={menuPlacement}
          menuPortalTarget={menuPortalTarget || (typeof document !== 'undefined' ? document.body : undefined)} // fix for layout settings
          menuPosition={menuPosition} // fix for layout settings
          openMenuOnFocus={openMenuOnFocus}
        />
      </>
    );
  },
);

Select.propTypes = {};
Select.defaultProps = {};

export default Select;
